import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { generateLocalizedUrl } from '@/lib/slugUtils';
import { supportedLanguages, type SupportedLanguage } from '@/lib/i18n';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import SEOHead from '@/components/SEOHead';
import Breadcrumb from '@/components/Breadcrumb';
import PageSpecificFAQStructuredData from '@/components/PageSpecificFAQStructuredData';
import { 
  Calculator, 
  TrendingUp, 
  Zap, 
  Users, 
  Globe, 
  Shield,
  ArrowRight,
  CheckCircle2
} from 'lucide-react';

export default function PricingGuide() {
  const { t, i18n } = useTranslation('pricing-guide');
  const navigate = useNavigate();
  const { lang } = useParams<{ lang: SupportedLanguage }>();
  const currentLang = lang && supportedLanguages.includes(lang) ? lang : 'en';

  const handleCTAClick = () => {
    const contactUrl = generateLocalizedUrl('contact', currentLang);
    navigate(contactUrl);
  };

  const factorIcons = [Calculator, TrendingUp, Zap, Users, Globe];

  return (
    <>
      <SEOHead
        title={t('meta.title')}
        description={t('meta.description')}
        lang={currentLang}
        path={generateLocalizedUrl('pricing-guide', currentLang)}
      />
      
      <Header />
      
      <div className="min-h-screen bg-gradient-to-b from-background to-secondary/10">
        <div className="container mx-auto px-4 py-8 max-w-5xl">
          <Breadcrumb />

          {/* Hero Section */}
          <section className="text-center mb-16 mt-12">
            <Badge variant="secondary" className="mb-6 text-base px-6 py-2" data-testid="badge-guide">
              <Calculator className="w-4 h-4 mr-2 inline" />
              {t('hero.badge')}
            </Badge>
            
            <h1 className="text-4xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-primary to-primary/60 bg-clip-text text-transparent" data-testid="title-hero">
              {t('hero.title')}
            </h1>
            
            <p className="text-xl md:text-2xl text-muted-foreground mb-4 font-medium" data-testid="text-subtitle">
              {t('hero.subtitle')}
            </p>
            
            <p className="text-lg text-muted-foreground mb-4 max-w-3xl mx-auto" data-testid="text-intro">
              {t('hero.intro')}
            </p>
            
            <p className="text-lg text-foreground mb-8 max-w-3xl mx-auto" data-testid="text-explanation">
              {t('hero.explanation')}
            </p>
            
            <Button size="lg" onClick={handleCTAClick} className="text-lg px-8 py-6" data-testid="button-cta-hero">
              {t('hero.cta')} <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </section>

          {/* Overview Section */}
          <Card className="mb-16">
            <CardContent className="p-8">
              <h2 className="text-2xl font-bold mb-6" data-testid="title-overview">{t('overview.title')}</h2>
              <ul className="space-y-3">
                {(t('overview.items', { returnObjects: true }) as string[]).map((item: string, index: number) => (
                  <li key={index} className="flex items-start" data-testid={`list-overview-${index}`}>
                    <CheckCircle2 className="w-5 h-5 text-primary mr-3 mt-0.5 flex-shrink-0" />
                    <span className="text-lg">{item}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>

          {/* What Is Section */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold mb-6" data-testid="title-what-is">{t('whatIs.title')}</h2>
            <p className="text-lg text-muted-foreground mb-6" data-testid="text-what-is-description">
              {t('whatIs.description')}
            </p>
            
            <Card>
              <CardContent className="p-8">
                <h3 className="text-xl font-semibold mb-4" data-testid="title-benefits">{t('whatIs.benefits.title')}</h3>
                <ul className="space-y-3 mb-6">
                  {(t('whatIs.benefits.items', { returnObjects: true }) as string[]).map((item: string, index: number) => (
                    <li key={index} className="flex items-start" data-testid={`list-benefit-${index}`}>
                      <CheckCircle2 className="w-5 h-5 text-primary mr-3 mt-0.5 flex-shrink-0" />
                      <span className="text-lg">{item}</span>
                    </li>
                  ))}
                </ul>
                <p className="text-lg font-medium" data-testid="text-conclusion">{t('whatIs.conclusion')}</p>
              </CardContent>
            </Card>
          </section>

          {/* Pricing Table Section */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold mb-4" data-testid="title-pricing">{t('pricing.title')}</h2>
            <p className="text-lg text-muted-foreground mb-8" data-testid="text-pricing-subtitle">{t('pricing.subtitle')}</p>
            
            <div className="overflow-x-auto mb-8">
              <table className="w-full border-collapse bg-card rounded-lg overflow-hidden">
                <thead>
                  <tr className="bg-primary/10">
                    <th className="p-4 text-left font-semibold" data-testid="header-type">{t('pricing.table.headers.type')}</th>
                    <th className="p-4 text-left font-semibold" data-testid="header-price">{t('pricing.table.headers.price')}</th>
                    <th className="p-4 text-left font-semibold" data-testid="header-example">{t('pricing.table.headers.example')}</th>
                    <th className="p-4 text-left font-semibold" data-testid="header-suited">{t('pricing.table.headers.suitedFor')}</th>
                  </tr>
                </thead>
                <tbody>
                  {(t('pricing.table.rows', { returnObjects: true }) as any[]).map((row: any, index: number) => (
                    <tr key={index} className="border-t border-border" data-testid={`row-pricing-${index}`}>
                      <td className="p-4 font-medium">{row.type}</td>
                      <td className="p-4 text-primary font-semibold">{row.price}</td>
                      <td className="p-4 text-sm text-muted-foreground">{row.example}</td>
                      <td className="p-4">{row.suitedFor}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <Card className="bg-primary/5 border-primary/20">
              <CardContent className="p-6">
                <p className="text-lg mb-2" data-testid="text-priceforms">{t('pricing.priceforms.text')}</p>
                <p className="text-lg font-bold text-primary" data-testid="text-priceforms-highlight">
                  👉 {t('pricing.priceforms.highlight')}
                </p>
              </CardContent>
            </Card>
          </section>

          {/* Factors Section */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold mb-4" data-testid="title-factors">{t('factors.title')}</h2>
            <p className="text-lg text-muted-foreground mb-8" data-testid="text-factors-subtitle">{t('factors.subtitle')}</p>
            
            <div className="grid gap-6">
              {(t('factors.items', { returnObjects: true }) as any[]).map((item: any, index: number) => {
                const Icon = factorIcons[index] || Calculator;
                return (
                  <Card key={index}>
                    <CardContent className="p-6">
                      <div className="flex items-start gap-4">
                        <div className="flex-shrink-0 w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                          <Icon className="w-6 h-6 text-primary" />
                        </div>
                        <div className="flex-1">
                          <h3 className="text-xl font-semibold mb-2" data-testid={`title-factor-${index}`}>
                            {item.number}. {item.title}
                          </h3>
                          <p className="text-muted-foreground" data-testid={`text-factor-${index}`}>{item.description}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </section>

          {/* Comparison Table Section */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold mb-8" data-testid="title-comparison">{t('comparison.title')}</h2>
            
            <div className="overflow-x-auto mb-6">
              <table className="w-full border-collapse bg-card rounded-lg overflow-hidden">
                <thead>
                  <tr className="bg-primary/10">
                    <th className="p-4 text-left font-semibold" data-testid="header-factor">{t('comparison.table.headers.factor')}</th>
                    <th className="p-4 text-left font-semibold" data-testid="header-nocode">{t('comparison.table.headers.nocode')}</th>
                    <th className="p-4 text-left font-semibold" data-testid="header-custom">{t('comparison.table.headers.custom')}</th>
                  </tr>
                </thead>
                <tbody>
                  {(t('comparison.table.rows', { returnObjects: true }) as any[]).map((row: any, index: number) => (
                    <tr key={index} className="border-t border-border" data-testid={`row-comparison-${index}`}>
                      <td className="p-4 font-medium">{row.factor}</td>
                      <td className="p-4 text-primary">{row.nocode}</td>
                      <td className="p-4 text-muted-foreground">{row.custom}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <p className="text-lg" data-testid="text-comparison-conclusion">
              👉 {t('comparison.conclusion')}
            </p>
          </section>

          {/* Investment Section */}
          <section className="mb-16">
            <Card className="bg-gradient-to-br from-primary/5 to-primary/10 border-primary/20">
              <CardContent className="p-8">
                <h2 className="text-3xl font-bold mb-6" data-testid="title-investment">{t('investment.title')}</h2>
                <p className="text-lg mb-8" data-testid="text-investment-scenario">{t('investment.scenario')}</p>
                
                <div className="bg-background/50 rounded-lg p-6 mb-6">
                  <h3 className="text-xl font-semibold mb-4" data-testid="title-example">{t('investment.example.title')}</h3>
                  <p className="mb-4" data-testid="text-example-company">{t('investment.example.company')}</p>
                  <ul className="space-y-2">
                    {(t('investment.example.results', { returnObjects: true }) as string[]).map((result: string, index: number) => (
                      <li key={index} className="flex items-center" data-testid={`list-result-${index}`}>
                        <TrendingUp className="w-5 h-5 text-primary mr-3" />
                        <span className="font-medium">{result}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <p className="text-lg font-medium" data-testid="text-investment-conclusion">{t('investment.conclusion')}</p>
              </CardContent>
            </Card>
          </section>

          {/* Transparency Section */}
          <section className="mb-16">
            <Card>
              <CardContent className="p-8">
                <div className="flex items-center gap-3 mb-6">
                  <Shield className="w-8 h-8 text-primary" />
                  <h2 className="text-2xl font-bold" data-testid="title-transparency">{t('transparency.title')}</h2>
                </div>
                <p className="text-lg mb-4" data-testid="text-transparency-intro">{t('transparency.intro')}</p>
                <ul className="space-y-3 mb-4">
                  {(t('transparency.items', { returnObjects: true }) as string[]).map((item: string, index: number) => (
                    <li key={index} className="flex items-start" data-testid={`list-transparency-${index}`}>
                      <CheckCircle2 className="w-5 h-5 text-primary mr-3 mt-0.5 flex-shrink-0" />
                      <span className="text-lg">{item}</span>
                    </li>
                  ))}
                </ul>
                <p className="text-lg font-medium" data-testid="text-transparency-conclusion">👉 {t('transparency.conclusion')}</p>
              </CardContent>
            </Card>
          </section>

          {/* FAQ Section */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold mb-8" data-testid="title-faq">{t('faq.title')}</h2>
            <div className="space-y-6">
              {(t('faq.items', { returnObjects: true }) as any[]).map((item: any, index: number) => (
                <Card key={index}>
                  <CardContent className="p-6">
                    <h3 className="text-xl font-semibold mb-3" data-testid={`question-${index}`}>
                      ❓ {item.question}
                    </h3>
                    <p className="text-muted-foreground" data-testid={`answer-${index}`}>{item.answer}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </section>

          {/* Final CTA Section */}
          <section className="text-center">
            <Card className="bg-gradient-to-r from-primary/10 to-primary/5 border-primary/20">
              <CardContent className="p-12">
                <h2 className="text-3xl font-bold mb-4" data-testid="title-final-cta">{t('finalCta.title')}</h2>
                <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto" data-testid="text-final-cta-subtitle">
                  {t('finalCta.subtitle')}
                </p>
                <div className="flex flex-col items-center gap-4">
                  <Button size="lg" onClick={handleCTAClick} className="text-lg px-8 py-6" data-testid="button-cta-final">
                    {t('finalCta.cta')} <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                  <p className="text-sm text-muted-foreground" data-testid="text-guarantee">{t('finalCta.guarantee')}</p>
                </div>
              </CardContent>
            </Card>
          </section>
        </div>
      </div>
      
      <PageSpecificFAQStructuredData namespace="pricing-guide" faqKey="faq.items" />
      <Footer />
    </>
  );
}
